/*
 * Copyright (c) CIRROM 2025.
 */

import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Alert, Box, Grid, Typography } from "@mui/material";
import { Heading, Wrapper } from "../components/wrapper/Wrapper";
import { THEMES } from "./constants";
import { MiraTheme } from "./theme";
import { green, grey, indigo } from "@mui/material/colors";
import { useTheme } from "./useTheme";
import React from "react";

type ThemeButtonType = {
  active?: boolean;
  theme?: MiraTheme;
};

const ThemeButton = styled.div<ThemeButtonType>`
  cursor: pointer;
  background: ${(props) => props.theme.palette.background.paper};
  height: 80px;
  border-radius: 0.3rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.825rem;
  position: relative;
  border: 1px solid
    ${(props) =>
      !props.active
        ? props.theme.palette.action.selected
        : props.theme.palette.action.active};
`;

type ThemeButtonInnerType = {
  selectedTheme: string;
  theme?: MiraTheme;
};

const ThemeButtonInner = styled.div<ThemeButtonInnerType>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  box-shadow: 0 0 0 1px ${(props) => props.theme.palette.action.selected};
  position: relative;

  ${(props) =>
    props.selectedTheme === THEMES.DEFAULT &&
    css`
      background: linear-gradient(-45deg, #23303f 50%, ${grey[100]} 0);
    `}
  ${(props) =>
    props.selectedTheme === THEMES.DARK &&
    css`
      background: #23303f;
    `}
  ${(props) =>
    props.selectedTheme === THEMES.LIGHT &&
    css`
      background: ${grey[100]};
    `}
  ${(props) =>
    props.selectedTheme === THEMES.BLUE &&
    css`
      background: linear-gradient(-45deg, #4782da 50%, ${grey[100]} 0);
    `}
  ${(props) =>
    props.selectedTheme === THEMES.GREEN &&
    css`
      background: linear-gradient(-45deg, ${green[500]} 50%, ${grey[100]} 0);
    `}
  ${(props) =>
    props.selectedTheme === THEMES.INDIGO &&
    css`
      background: linear-gradient(-45deg, ${indigo[500]} 50%, ${grey[100]} 0);
    `}
  ${(props) =>
    props.selectedTheme === THEMES.CIRROM &&
    css`
      background: linear-gradient(-45deg, #ffbf23 50%, ${grey[100]} 0);
    `}
`;

const ThemeTitle = styled(Typography)`
  text-align: center;
`;

type ThemeTypes = {
  title: string;
  themeVariant: string;
};

function ThemeItem({ title, themeVariant }: ThemeTypes) {
  const { theme, setTheme } = useTheme();

  return (
    <Grid size={6}>
      <ThemeButton
        active={themeVariant === theme}
        onClick={() => setTheme(themeVariant)}
      >
        <ThemeButtonInner selectedTheme={themeVariant} />
      </ThemeButton>
      <ThemeTitle variant="subtitle2" gutterBottom>
        {title}
      </ThemeTitle>
    </Grid>
  );
}

export function ThemeSelector() {
  return (
    <Wrapper>
      <Heading>Theme Settings</Heading>

      <Box px={6} my={3}>
        <Alert icon={false} severity="info">
          <strong>Hello!</strong> Select your style below. Choose the ones that
          best fits your needs.
        </Alert>
      </Box>

      <Box px={6} my={3}>
        <Grid container spacing={3}>
          <ThemeItem title="Dark" themeVariant={THEMES.DARK} />
          <ThemeItem title="Light" themeVariant={THEMES.LIGHT} />
          <ThemeItem title="Default" themeVariant={THEMES.DEFAULT} />
          <ThemeItem title="Blue" themeVariant={THEMES.BLUE} />
          <ThemeItem title="Green" themeVariant={THEMES.GREEN} />
          <ThemeItem title="Indigo" themeVariant={THEMES.INDIGO} />
          <ThemeItem title="Cirrom" themeVariant={THEMES.CIRROM} />
        </Grid>
      </Box>
    </Wrapper>
  );
}
